import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../../components/UI/Layouts";
import Content from "../../components/UI/Layouts/Content";
import { AdCircuit, EditAdCircuit } from "../../types";
import PageHeader from "../../components/common/PageHeader";
import { useTranslation } from "react-i18next";
import { useStores } from "../../hooks/useStores";
import { VirtualizedTable } from "../../components/common/VirtualizedTable";
import { Link } from "react-router-dom";
import { Breadcrumb, BreadcrumbItem } from "../../components/UI/Breadcrumb";
import { useSnackbar } from "notistack";
import { observer } from "mobx-react";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import PopupState, { bindTrigger, bindPopover } from "material-ui-popup-state";
import DeleteIcon from "@mui/icons-material/Delete";
import EmptySpace from "../../components/common/EmptySpace";
import Loading from "../../components/common/Loading";
import AdvertisingCircuitModal from "../../components/common/AdvertisingCircuit/AdvertisingCircuitModal/AdvertisingCircuitModal";

const AdvertisingCircuit: React.FC = observer(() => {
  const { adCircuitStore } = useStores();
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const modalHandler = () => {
    setIsModalOpen((open) => !open);
  };
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    setIsLoading(true);
    adCircuitStore
      .getAdCircuits()
      .then(() => setIsLoading(false))
      .catch((err: any) => {
        enqueueSnackbar(err.message, {
          variant: "error",
        });
      });
  }, []);

  const handleSubmit = useCallback(
    async (data: EditAdCircuit) => {
      setIsLoading(true);

      try {
        await adCircuitStore.addAdCircuit(data).then(() => {
          setIsLoading(false);
        });
        enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
          variant: "success",
        });
      } catch (error: any) {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      }

      modalHandler();
    },
    [adCircuitStore.adCircuits]
  );

  const handleDeleteAdvCircuit = useCallback(
    async (id: string) => {
      setIsLoading(true);

      try {
        await adCircuitStore.deleteAdCircuits(id).then(() => {
          setIsLoading(false);
        });
        enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
          variant: "success",
        });
      } catch (error: any) {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      }
    },
    [adCircuitStore.adCircuits]
  );

  const columns = useMemo(
    () => [
      {
        width: 80,
        flexGrow: 2,
        label: t("screens.advertisingCircuit.column.title"),
        dataKey: "title",
        sorter: (a: { name: string }, b: { name: any }) =>
          a.name.localeCompare(b.name),
        render: (name: string, adCircuit: AdCircuit) => {
          return <Link to={"/advertise/" + adCircuit.id}>{name}</Link>;
        },
      },
      {
        width: 90,
        flexGrow: 0,
        label: t("common.action"),
        dataKey: "id",
        render: (id: string) => {
          return (
            <>
              <PopupState variant="popover" popupId="demo-popup-popover">
                {(popupState) => (
                  <div>
                    <IconButton color="error" {...bindTrigger(popupState)}>
                      <DeleteIcon />
                    </IconButton>
                    <Popover
                      {...bindPopover(popupState)}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "center",
                      }}
                    >
                      <Typography sx={{ p: 1 }}>
                        {t("common.deletePopUp")}
                        <IconButton
                          color="error"
                          sx={{ p: 0 }}
                          onClick={() => handleDeleteAdvCircuit(id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Typography>
                    </Popover>
                  </div>
                )}
              </PopupState>
            </>
          );
        },
      },
    ],
    [t]
  );

  return (
    <Layout hideFooter>
      <Content borderBottomWidth="1px" align="top">
        <Breadcrumb my={"20px"}>
          <BreadcrumbItem path="/">
            {t("screens.advertisingCircuit.breadcrumb.home")}
          </BreadcrumbItem>
          <BreadcrumbItem active>
            {t("screens.advertisingCircuit.breadcrumb.advCircuit")}
          </BreadcrumbItem>
        </Breadcrumb>

        <PageHeader
          title={t("screens.advertisingCircuit.pageHeader.title")}
          buttonEnable={true}
          buttonText={t("screens.advertisingCircuit.pageHeader.buttonText")}
          handleButton={modalHandler}
        />
      </Content>
      {isLoading ? (
        <Loading />
      ) : !isLoading && adCircuitStore.adCircuits.length > 0 ? (
        <Content fullHeight align="top" mt={[null, null, null, "0px"]}>
          <div
            style={{
              flex: "1 1 auto",
              width: "100%",
              height: `${(adCircuitStore.adCircuits.length + 1) * 50}px`,
              display: "flex",
              marginTop: "20px",
            }}
          >
            <VirtualizedTable
              rowCount={adCircuitStore.adCircuits.length}
              rowGetter={({ index }) => adCircuitStore.adCircuits[index]}
              columns={columns}
              rowStyle={(row) =>
                row.index % 2 === 0 ? { background: "#EDF1FB" } : {}
              }
            />
          </div>
        </Content>
      ) : (
        <EmptySpace
          title={t("screens.advertisingCircuit.emptyData")}
          buttonEnable={true}
          buttonText={t("screens.advertisingCircuit.pageHeader.buttonText")}
          handleButton={modalHandler}
        />
      )}

      <AdvertisingCircuitModal
        adCircuit={undefined}
        isSubmitting={false}
        isOpen={isModalOpen}
        onClose={modalHandler}
        onSubmit={handleSubmit}
      />
    </Layout>
  );
});

export default AdvertisingCircuit;
